import { FC, useEffect, useState } from 'react'

import styled from 'styled-components'
import { useForm, SubmitHandler } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useParams } from 'react-router-dom'

import {
  useGetAllClientsQuery,
  useUpdateClientMutation,
} from 'src/Redux/api/clientsApi.ts'
import { useGetOfferByIdQuery } from 'src/Redux/api/offersApi.ts'
import { useGetAllSettingsQuery } from 'src/Redux/api/settingsApi.ts'
import { colors, device } from 'src/Helpers/constants.ts'
import { WORK_HOURS_PER_MONTH } from 'src/Pages/Calculator/useCalculator.tsx'
import { PageTitle } from 'src/Components/PageTitle/index.tsx'
import { ModelsList } from 'src/Pages/Calculator/ModelsList.tsx'
import Save from 'src/Assets/save.svg?react'
import { IconButton } from 'src/Components/Button/IconButton.tsx'
import { DownloadPdfButton } from 'src/Pages/Calculator/DownloadPdfButton.tsx'
import { DownloadTakumiPdfButton } from 'src/Pages/Calculator/DownloadTakumiPdfButton.tsx'
import { useGetAllModelsQuery } from 'src/Redux/api/modelApi'
import { useGetProfileQuery } from 'src/Redux/api/authApi.ts'
import dayjs from 'src/Helpers/dayjs.ts'
import { formatClientName } from 'src/Helpers/organizationalForm'

import { MainInfoForm } from './MainInfoForm'
import { useEquipmentItems } from './useEquipmentItems'
import { useChangeTracking } from './useChangeTracking'
import { useOfferOperations } from './useOfferOperations'
import { useOfferPreFill } from './useOfferPreFill'
import { useCalculator } from './useCalculator'
import {
  getDefaultFormValues,
  getSettingValue,
  transformEquipmentForPrint,
  OfferFormData,
} from './helpers'
import { offerFormSchema } from './validation'
import {
  applyConflicts,
  ContactConflict,
  OfferContact,
  planClientContactSync,
} from './clientContactSync'
import { ClientContactSyncModal } from './ClientContactSyncModal'

const Separator = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${colors.secondGray};

  @media ${device.tabletMax} {
    display: none;
  }
`

const ButtonsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  justify-content: center;
`

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 50px;
  position: static;

  @media ${device.tabletMax} {
    gap: 20px;
  }
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 36px;
  position: relative;
  height: 100%;
  overflow: hidden;

  @media ${device.tabletMax} {
    gap: 20px;
  }
`

const InnerContainer = styled.div`
  height: 100%;
  width: 100%;
  overflow: auto;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
`

const Text = styled.span`
  font-size: 12px;
  width: 100%;
  text-align: left;
  color: ${colors.thirdGray};
  font-weight: 400;
`

const StyledButton = styled(IconButton)`
  background-color: ${colors.fourthBlue} !important;

  &:hover {
    background-color: ${colors.fourthBlue} !important;
  }
`

export const CalculatorPage: FC = () => {
  const { id: offerId } = useParams<{ id: string }>()
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [equipmentError, setEquipmentError] = useState<string>('')
  const { data: settingsList } = useGetAllSettingsQuery()

  // Extract settings values
  const privilegesData = getSettingValue(settingsList || [], 'privilegesData')
  const introductionText = getSettingValue(
    settingsList || [],
    'introductionText',
  )
  const vatRate = Number(getSettingValue(settingsList || [], 'vatRate')) || 22
  const medicalExaminationCost =
    Number(getSettingValue(settingsList || [], 'medicalExaminationCost')) || 0

  // Form management
  const { control, handleSubmit, watch, reset, setValue, getValues } =
    useForm<OfferFormData>({
      defaultValues: getDefaultFormValues(),
      resolver: yupResolver(offerFormSchema) as never,
    })

  const formValues = watch()

  const {
    fuelPrice,
    partDeliveryPrice,
    personDeliveryPrice,
    accommodationPrice,
    foodPrice,
    createDate,
    clientId,
    clientDivision,
    minRentPeriod,
    validityDaysCount,
    personalBenefits,
    dailyMedCheckCost,
    clientContactName,
    clientContactNamePosition,
    season,
    personnelHouseholdServicesCost,
  } = formValues

  // Data fetching
  const { data: offerData } = useGetOfferByIdQuery(Number(offerId), {
    skip: !offerId,
  })
  const { data: user } = useGetProfileQuery()
  const { getTotalRent } = useCalculator()

  // Equipment items management
  const {
    items,
    setItems,
    calculatedEquipments,
    addItem,
    removeItem,
    updateItemProperty,
    addUnit,
    removeUnit,
    updateUnitProperty,
  } = useEquipmentItems({
    fuelPrice,
    partDeliveryPrice,
    personDeliveryPrice,
    accommodationPrice: accommodationPrice || 0,
    foodPrice: foodPrice || 0,
    personnelHouseholdServicesCost: Number(personnelHouseholdServicesCost) || 0,
    dailyMedCheckCost,
    season,
    settingsList,
  })

  // Change tracking
  const { hasChanges, setInitialValues } = useChangeTracking({
    offerId,
    formValues,
    items,
    personnelHouseholdServicesCost: Number(personnelHouseholdServicesCost) || 0,
  })

  // Offer operations
  const { saveOffer, isLoading } = useOfferOperations({
    offerId,
    setInitialValues,
  })

  // Pre-fill form when offer data is loaded
  useOfferPreFill({
    offerData,
    reset,
    getTotalRent,
    medicalExaminationCost,
    setItems,
    setInitialValues,
  })

  const { data: clientsResponse } = useGetAllClientsQuery({
    pageNumber: 1,
    all: true,
  })

  const { data: modelResponse } = useGetAllModelsQuery({
    all: true,
  })

  // Event handlers
  const onItemClick = (equipmentId: string | null): void => {
    if (!equipmentId || !modelResponse) return

    const selectedEquipment = modelResponse?.data?.find(
      (model) => model.id.toString() === equipmentId,
    )

    if (selectedEquipment) {
      addItem(selectedEquipment)
      // Clear equipment error when equipment is added
      setEquipmentError('')
    }
  }

  const onDeleteClick = (itemId: number): void => {
    removeItem(itemId)
  }

  const onSaveOffer: SubmitHandler<OfferFormData> = async (data) => {
    // Clear previous equipment error
    setEquipmentError('')

    // Check if any equipment is selected
    if (calculatedEquipments.length === 0) {
      setEquipmentError('Выберите технику для расчета')
      return
    }

    const isSaved = await saveOffer(data, calculatedEquipments)
    if (isSaved) syncClientContact(data)
  }

  const equipmentToPrint = transformEquipmentForPrint(
    calculatedEquipments,
    formValues,
    Number(personnelHouseholdServicesCost) || 0,
    WORK_HOURS_PER_MONTH,
    vatRate,
  )

  const selectedClient = clientsResponse?.data?.find((item) => {
    return item.id === Number(clientId)
  })

  const [updateClient] = useUpdateClientMutation()
  const [pendingContactSync, setPendingContactSync] = useState<{
    clientId: number
    silentUpdates: OfferContact
    updates: OfferContact
    conflicts: ContactConflict[]
  } | null>(null)

  const syncClientContact = (data: OfferFormData): void => {
    const client = clientsResponse?.data?.find(
      (item) => item.id === Number(data.clientId),
    )
    if (!client) return

    const plan = planClientContactSync(client, {
      offerContactName: data.clientContactName,
      offerContactPosition: data.clientContactNamePosition,
    })

    if (plan.conflicts.length > 0) {
      setPendingContactSync({
        clientId: client.id,
        silentUpdates: plan.silentUpdates,
        updates: applyConflicts(plan),
        conflicts: plan.conflicts,
      })
      return
    }

    if (Object.keys(plan.silentUpdates).length > 0) {
      updateClient({ id: client.id, data: plan.silentUpdates })
    }
  }

  const onConfirmContactSync = (): void => {
    if (pendingContactSync) {
      updateClient({
        id: pendingContactSync.clientId,
        data: pendingContactSync.updates,
      })
    }
    setPendingContactSync(null)
  }

  const onCancelContactSync = (): void => {
    if (
      pendingContactSync &&
      Object.keys(pendingContactSync.silentUpdates).length > 0
    ) {
      updateClient({
        id: pendingContactSync.clientId,
        data: pendingContactSync.silentUpdates,
      })
    }
    setPendingContactSync(null)
  }

  useEffect(() => {
    if (!selectedClient) return

    if (!getValues('clientContactName') && selectedClient.offerContactName) {
      setValue('clientContactName', selectedClient.offerContactName)
    }
    if (
      !getValues('clientContactNamePosition') &&
      selectedClient.offerContactPosition
    ) {
      setValue('clientContactNamePosition', selectedClient.offerContactPosition)
    }
  }, [selectedClient, getValues, setValue])

  const isWorkService = Boolean(
    accommodationPrice && accommodationPrice > 0 && foodPrice && foodPrice > 0,
  )

  const formattedOfferDate = dayjs(createDate).format('DD.MM.YYYY')

  const withoutMedicalExamination = !dailyMedCheckCost

  const isMobDemob =
    equipmentToPrint &&
    Boolean(
      equipmentToPrint.find((el) =>
        (el?.units || []).some(
          (unit) =>
            unit.deliveryCost ||
            unit.returnCost ||
            unit.departurePoint ||
            unit.workPoint ||
            unit.returnPoint,
        ),
      ),
    )

  return (
    <Container>
      <TitleWrapper>
        <PageTitle>Коммерческое предложение</PageTitle>
      </TitleWrapper>
      <InnerContainer>
        <MainInfoForm clients={clientsResponse?.data} control={control} />

        <Separator />

        <ModelsList
          models={modelResponse?.data}
          onItemClick={onItemClick}
          setSearchTerm={setSearchTerm}
          searchTerm={searchTerm}
          items={calculatedEquipments}
          onDeleteClick={onDeleteClick}
          updateItemProperty={updateItemProperty}
          equipmentError={equipmentError}
          onAddUnit={addUnit}
          onRemoveUnit={removeUnit}
          onUpdateUnit={updateUnitProperty}
        />

        <ButtonsWrapper>
          <StyledButton
            onClick={handleSubmit(onSaveOffer)}
            disabled={isLoading || (offerId ? !hasChanges : false)}
            tooltiptext={'Сохранить КП'}
            data-testid='save-offer'
          >
            <Save />
          </StyledButton>

          <DownloadPdfButton
            offerEquipments={equipmentToPrint}
            offerDate={formattedOfferDate}
            clientContactName={clientContactName}
            clientContactNamePosition={clientContactNamePosition}
            clientName={formatClientName(selectedClient)}
            clientDivision={clientDivision}
            minRentPeriod={minRentPeriod}
            offerNumber={offerData?.offerNumber}
            validityDaysCount={validityDaysCount}
            isWorkService={isWorkService}
            personalBenefits={personalBenefits}
            privilegesData={privilegesData as Array<string>}
            introductionText={introductionText as string}
            firstName={user?.firstName || ''}
            lastName={user?.lastName || ''}
            email={user?.email || ''}
            phone={user?.phone || ''}
            disabled={!offerId || hasChanges || isLoading}
            withoutMedicalExamination={withoutMedicalExamination}
            isMobDemob={isMobDemob}
            vatRate={vatRate}
          />

          <DownloadTakumiPdfButton
            offerEquipments={equipmentToPrint}
            offerDate={formattedOfferDate}
            clientContactName={clientContactName}
            clientContactNamePosition={clientContactNamePosition}
            clientName={formatClientName(selectedClient)}
            clientDivision={clientDivision}
            minRentPeriod={minRentPeriod}
            offerNumber={offerData?.offerNumber}
            validityDaysCount={validityDaysCount}
            isWorkService={isWorkService}
            personalBenefits={personalBenefits}
            privilegesData={privilegesData as Array<string>}
            introductionText={introductionText as string}
            firstName={user?.firstName || ''}
            lastName={user?.lastName || ''}
            email={user?.email || ''}
            phone={user?.phone || ''}
            disabled={!offerId || hasChanges || isLoading}
            withoutMedicalExamination={withoutMedicalExamination}
            isMobDemob={isMobDemob}
            vatRate={vatRate}
          />
        </ButtonsWrapper>

        <Text>{`Ответственный менеджер: ${user?.firstName} ${user?.lastName}`}</Text>
      </InnerContainer>
      <ClientContactSyncModal
        clientName={selectedClient?.name}
        conflicts={pendingContactSync?.conflicts || []}
        onConfirm={onConfirmContactSync}
        onCancel={onCancelContactSync}
      />
    </Container>
  )
}
